/**
 * Song-page settings and events for the sheet viewer that travel past the annotation workspace
 * (annotate/SheetWorkspace renders the viewer with fixed props): the page to open a file at in, and
 * the two-up eligibility, "next beyond the last page", "last page in view" and "page shown" events
 * out. The page layout itself (Samfellt / Heil síða / Tvær síður) is a device preference read from
 * viewer/pageMode.
 *
 * Provide a STABLE value (memoised; callbacks may change identity — the viewer reads them through
 * a ref): every new value re-renders the viewer.
 */
import { createContext } from 'react';

/** How "next" was asked for. */
export type TurnVia = 'key' | 'tap' | 'swipe' | 'button';

export interface NextPastEndEvent {
  /** A key / Bluetooth pedal, the right third of a paged view, a swipe, or a page button (edit-mode toolbar). */
  via: TurnVia;
  /** An auto-repeated keydown (key or pedal held down): never opens anything. */
  repeat: boolean;
}

/**
 * What the song page did with "next" beyond the end (viewer/setlist nextPastEndAction): 'none' =
 * nothing follows, so a tap or swipe shows the "Síðasta síða" hint; anything else (or void) = handled.
 */
export type NextPastEndResult = 'show' | 'open' | 'none';

export interface SheetViewerEnv {
  /** The viewer's size allows two-up (landscape, ≥ 1000 CSS px wide) — changes on resize. */
  onTwoUpEligible?: (eligible: boolean) => void;
  /** Next (key, pedal, tap zone, swipe) was requested on the last page / spread. */
  onNextPastEnd?: (e: NextPastEndEvent) => NextPastEndResult | void;
  /** The last page came into / went out of view. */
  onAtEnd?: (atEnd: boolean) => void;
  /**
   * The page (0-based) to open a file at when the song page has no reading position for it yet
   * (song tabs); null / absent = page 1. Asked once per file id per song page; clamped to the page count.
   */
  initialPage?: (fileId: string) => number | null;
  /** The current page (0-based) of the showing file revision changed (also called for the first page shown). */
  onPage?: (fileId: string, rev: number, page: number) => void;
}

/** Without a provider the viewer uses the device's page-layout preference and reports nothing. */
export const SheetViewerEnvContext = createContext<SheetViewerEnv>({});
